import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useClinic } from '@/contexts/ClinicContext';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { patientsApi } from '@/services/api';
import { INDIAN_STATES } from '@/constants';
import { toast } from 'sonner';
import { formatDateOnly, getKolkataTodayString } from '@/utils/dateTime';
import {
  ArrowLeft,
  Loader2,
  User,
  Phone,
  Mail,
  MapPin,
  Heart,
  AlertTriangle,
  CreditCard,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

const calculateAgeFromDob = (dobStr) => {
  if (!dobStr) return '';
  const birthDate = new Date(dobStr);
  if (isNaN(birthDate.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age >= 0 ? String(age) : '0';
};

const patientSchema = z.object({
  patientCode: z.string().optional(),
  status: z.enum(['ACTIVE', 'INACTIVE', 'ARCHIVED']).optional(),
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(1, 'Last name must be at least 1 character'),
  gender: z.enum(['male', 'female', 'other'], { required_error: 'Please select a gender' }),
  dateOfBirth: z.string().optional(),
  age: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email('Please enter a valid email').or(z.literal('')).optional(),
  bloodGroup: z.string().optional(),
  aadharNo: z.string().optional(),
  address: z.object({
    street: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    pinCode: z.string().regex(/^[1-9][0-9]{5}$/, 'Enter valid 6-digit PIN code').or(z.literal('')),
  }).optional(),
  emergencyContact: z.object({
    name: z.string().optional(),
    phone: z.string().optional(),
    relationship: z.string().optional(),
  }).optional(),
  allergies: z.string().optional(),
  chronicConditions: z.string().optional(),
});

const PatientFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { buildUrl, hospitalId } = useClinic();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [countryCode, setCountryCode] = useState('+91');
  const [phoneNumber, setPhoneNumber] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(patientSchema),
    defaultValues: {
      patientCode: '',
      status: 'ACTIVE',
      firstName: '',
      lastName: '',
      gender: undefined,
      dateOfBirth: '',
      age: '',
      phone: '',
      email: '',
      bloodGroup: '',
      aadharNo: '',
      address: {
        street: '',
        city: '',
        state: '',
        pinCode: '',
      },
      emergencyContact: {
        name: '',
        phone: '',
        relationship: '',
      },
      allergies: '',
      chronicConditions: '',
    },
  });

  useEffect(() => {
    const full = phoneNumber.trim() ? `${countryCode.trim()}${phoneNumber.trim()}` : '';
    setValue('phone', full);
  }, [countryCode, phoneNumber, setValue]);

  const handleDobChange = (e) => {
    const newDob = e.target.value;
    setValue('dateOfBirth', newDob, { shouldValidate: true });
    if (newDob) {
      const calculatedAge = calculateAgeFromDob(newDob);
      setValue('age', calculatedAge, { shouldValidate: true });
    }
  };

  const handleAgeChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    const num = digits ? Math.min(150, parseInt(digits, 10)) : '';
    setValue('age', num !== '' ? String(num) : '', { shouldValidate: true });
  };

  // Validation state for Save button
  const isIndia = countryCode.trim() === '+91' || countryCode.trim() === '91';
  const isPhoneRed = isIndia && phoneNumber.length > 0 && phoneNumber.length !== 10;
  const currentAge = (watch('age') || '').toString().trim();
  const currentDob = (watch('dateOfBirth') || '').trim();
  const isAgeOrDobValid = (currentAge !== '' && !isNaN(Number(currentAge)) && Number(currentAge) >= 0) || currentDob !== '';

  const currentEmail = (watch('email') || '').trim();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const hasValidEmail = currentEmail.length > 0 && emailRegex.test(currentEmail);
  const hasValidPhone = isIndia
    ? phoneNumber.length === 10
    : (phoneNumber.length >= 7 && phoneNumber.length <= 15);

  const isPhoneFieldValid = phoneNumber.length === 0 || hasValidPhone;
  const isEmailFieldValid = currentEmail.length === 0 || hasValidEmail;
  const isContactValid = (hasValidPhone || hasValidEmail) && isPhoneFieldValid && isEmailFieldValid;

  const firstName = (watch('firstName') || '').trim();
  const lastName = (watch('lastName') || '').trim();
  const gender = watch('gender');

  const isFormValid =
    firstName.length >= 2 &&
    lastName.length >= 1 &&
    Boolean(gender) &&
    isAgeOrDobValid &&
    isContactValid;

  useEffect(() => {
    if (isEditMode) {
      loadPatient();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode]);

  const loadPatient = async () => {
    try {
      setLoading(true);
      const patient = await patientsApi.getById(id, hospitalId);
      
      setValue('patientCode', patient.patientCode || '');
      setValue('status', (patient.status || 'ACTIVE').toUpperCase());
      setValue('firstName', patient.firstName || '');
      setValue('lastName', patient.lastName || '');
      setValue('gender', patient.gender || undefined);
      
      const dobStr = patient.dateOfBirth || patient.dob ? formatDateOnly(patient.dateOfBirth || patient.dob, 'YYYY-MM-DD') : '';
      setValue('dateOfBirth', dobStr);

      if (patient.age !== undefined && patient.age !== null && patient.age !== '') {
        setValue('age', String(patient.age));
      } else if (dobStr) {
        setValue('age', calculateAgeFromDob(dobStr));
      } else {
        setValue('age', '');
      }

      // Parse phone into countryCode and phoneNumber
      const rawPhone = patient.phone || patient.contactNumber || '';
      if (rawPhone.trim()) {
        const cleanRaw = rawPhone.trim();
        if (cleanRaw.startsWith('+91')) {
          setCountryCode('+91');
          setPhoneNumber(cleanRaw.slice(3).replace(/\D/g, ''));
        } else if (cleanRaw.startsWith('+')) {
          const match = cleanRaw.match(/^(\+\d{1,4})\s*(.*)$/);
          if (match) {
            setCountryCode(match[1]);
            setPhoneNumber(match[2].replace(/\D/g, ''));
          } else {
            setCountryCode('+91');
            setPhoneNumber(cleanRaw.replace(/\D/g, ''));
          }
        } else {
          setCountryCode('+91');
          setPhoneNumber(cleanRaw.replace(/\D/g, ''));
        }
      } else {
        setCountryCode('+91');
        setPhoneNumber('');
      }

      setValue('email', patient.email || '');
      setValue('bloodGroup', patient.bloodGroup || '');
      setValue('aadharNo', patient.aadharNo || '');
      setValue('address.street', patient.address?.street || '');
      setValue('address.city', patient.address?.city || '');
      setValue('address.state', patient.address?.state || '');
      setValue('address.pinCode', patient.address?.pinCode || '');
      setValue('emergencyContact.name', patient.emergencyContact?.name || '');
      setValue('emergencyContact.phone', patient.emergencyContact?.phone || '');
      setValue('emergencyContact.relationship', patient.emergencyContact?.relationship || '');
      setValue('allergies', patient.allergies?.join(', ') || '');
      setValue('chronicConditions', patient.chronicConditions?.join(', ') || '');
    } catch (error) {
      toast.error('Failed to load patient');
      navigate(buildUrl('patients'));
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setSaving(true);
      
      const fullPhone = phoneNumber.trim() ? `${countryCode.trim()}${phoneNumber.trim()}` : undefined;

      // Transform data
      const patientData = {
        ...data,
        phone: fullPhone,
        age: data.age !== undefined && data.age !== '' ? parseInt(data.age, 10) : undefined,
        dateOfBirth: data.dateOfBirth?.trim() || undefined,
        email: data.email?.trim() || undefined,
        allergies: data.allergies ? data.allergies.split(',').map(s => s.trim()).filter(Boolean) : [],
        chronicConditions: data.chronicConditions ? data.chronicConditions.split(',').map(s => s.trim()).filter(Boolean) : [],
      };
      
      if (isEditMode) {
        await patientsApi.update(id, patientData, hospitalId);
        toast.success('Patient updated successfully');
      } else {
        await patientsApi.create(patientData, hospitalId);
        toast.success('Patient created successfully');
      }
      
      navigate(buildUrl('patients'));
    } catch (error) {
      toast.error(error.message || 'Failed to save patient');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="patient-form-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="patient-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(buildUrl('patients'))}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">
            {isEditMode ? 'Edit Patient' : 'Add New Patient'}
          </h1>
          <p className="text-slate-500">
            {isEditMode ? 'Update patient information' : 'Register a new patient in the system'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="h-5 w-5 text-teal-600" />
              Personal Information
            </CardTitle>
            <CardDescription>Basic patient details</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="patientCode">
                Patient Code / Hospital MRN <span className="text-xs text-slate-400 font-normal">(Optional ID)</span>
              </Label>
              <Input
                id="patientCode"
                placeholder="e.g. PAT-001 or MRN-1029"
                {...register('patientCode')}
                data-testid="input-patientCode"
                className="font-mono"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Patient Status</Label>
              <Select
                value={watch('status') || 'ACTIVE'}
                onValueChange={(value) => setValue('status', value, { shouldValidate: true })}
              >
                <SelectTrigger data-testid="select-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">Active (Eligible for Visits)</SelectItem>
                  <SelectItem value="INACTIVE">Inactive (Temporarily Suspended)</SelectItem>
                  <SelectItem value="ARCHIVED">Archived / Discharged</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                placeholder="Enter first name"
                {...register('firstName')}
                data-testid="input-firstName"
              />
              {errors.firstName && (
                <p className="text-sm text-red-500">{errors.firstName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                placeholder="Enter last name"
                {...register('lastName')}
                data-testid="input-lastName"
              />
              {errors.lastName && (
                <p className="text-sm text-red-500">{errors.lastName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="gender">Gender *</Label>
              <Select
                value={watch('gender')}
                onValueChange={(value) => setValue('gender', value, { shouldValidate: true })}
              >
                <SelectTrigger data-testid="select-gender">
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              {errors.gender && (
                <p className="text-sm text-red-500">{errors.gender.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="age">
                Age {!watch('dateOfBirth') && <span className="text-red-500">*</span>}
              </Label>
              <Input
                id="age"
                type="text"
                inputMode="numeric"
                placeholder="Enter age"
                value={watch('age') || ''}
                onChange={handleAgeChange}
                maxLength={3}
                data-testid="input-age"
              />
              {errors.age && (
                <p className="text-sm text-red-500">{errors.age.message}</p>
              )}
              {!watch('age') && !watch('dateOfBirth') && (
                <p className="text-xs text-slate-500">Age or Date of Birth is required</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="dateOfBirth">
                Date of Birth <span className="text-xs font-normal text-slate-400">(Optional)</span>
              </Label>
              <Input
                id="dateOfBirth"
                type="date"
                max={getKolkataTodayString()}
                value={watch('dateOfBirth') || ''}
                onChange={handleDobChange}
                data-testid="input-dob"
              />
              {errors.dateOfBirth && (
                <p className="text-sm text-red-500">{errors.dateOfBirth.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="bloodGroup">Blood Group</Label>
              <Select
                value={watch('bloodGroup')}
                onValueChange={(value) => setValue('bloodGroup', value)}
              >
                <SelectTrigger data-testid="select-bloodGroup">
                  <SelectValue placeholder="Select blood group" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="A+">A+</SelectItem>
                  <SelectItem value="A-">A-</SelectItem>
                  <SelectItem value="B+">B+</SelectItem>
                  <SelectItem value="B-">B-</SelectItem>
                  <SelectItem value="AB+">AB+</SelectItem>
                  <SelectItem value="AB-">AB-</SelectItem>
                  <SelectItem value="O+">O+</SelectItem>
                  <SelectItem value="O-">O-</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="aadharNo" className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-slate-500" />
                Aadhaar Number
              </Label>
              <Input
                id="aadharNo"
                placeholder="XXXX-XXXX-XXXX"
                {...register('aadharNo')}
                data-testid="input-aadhar"
              />
              <p className="text-xs text-slate-500">Optional - for identification purposes</p>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Phone className="h-5 w-5 text-teal-600" />
              Contact Information
            </CardTitle>
            <CardDescription>
              How to reach the patient (at least one contact method is required)
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="phone">
                Phone Number {!currentEmail && <span className="text-red-500">*</span>}
              </Label>
              <div className="flex gap-2">
                <div className="w-24 shrink-0">
                  <Input
                    id="countryCode"
                    value={countryCode}
                    onChange={(e) => {
                      let val = e.target.value.replace(/[^\d+]/g, '');
                      if (val && !val.startsWith('+')) {
                        val = `+${val}`;
                      }
                      setCountryCode(val);
                    }}
                    placeholder="+91"
                    maxLength={5}
                    className="text-center font-medium"
                    data-testid="input-country-code"
                  />
                </div>
                <div className="flex-1">
                  <Input
                    id="phone"
                    type="tel"
                    inputMode="numeric"
                    value={phoneNumber}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '');
                      setPhoneNumber(isIndia ? digits.slice(0, 10) : digits.slice(0, 15));
                    }}
                    placeholder={isIndia ? '10-digit mobile number' : 'Phone number'}
                    className={`w-full transition-colors ${
                      isPhoneRed
                        ? 'border-red-500 ring-1 ring-red-500 focus-visible:ring-red-500 bg-red-50/20 text-red-900'
                        : ''
                    }`}
                    data-testid="input-phone"
                  />
                </div>
              </div>
              {isPhoneRed && (
                <p className="text-xs text-red-500 font-medium">
                  {phoneNumber.length}/10 digits entered. Exactly 10 digits required for +91
                </p>
              )}
              {!isPhoneRed && !currentEmail && !phoneNumber && (
                <p className="text-xs text-slate-500">Either phone number or email is mandatory</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">
                Email {!phoneNumber.trim() && <span className="text-red-500">*</span>}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="patient@email.com"
                {...register('email')}
                data-testid="input-email"
              />
              {errors.email && (
                <p className="text-sm text-red-500">{errors.email.message}</p>
              )}
              {!watch('email')?.trim() && !phoneNumber.trim() && (
                <p className="text-xs text-slate-500">Either email or phone number is mandatory</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Address */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapPin className="h-5 w-5 text-teal-600" />
              Address
            </CardTitle>
            <CardDescription>Patient's residential address</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="street">Street Address / Locality</Label>
              <Input
                id="street"
                placeholder="House No., Street, Area, Landmark"
                {...register('address.street')}
                data-testid="input-street"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="city">City / District</Label>
              <Input
                id="city"
                placeholder="e.g., Mumbai, Delhi, Bangalore"
                {...register('address.city')}
                data-testid="input-city"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="state">State</Label>
              <Select
                value={watch('address.state')}
                onValueChange={(value) => setValue('address.state', value)}
              >
                <SelectTrigger data-testid="select-state">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {INDIAN_STATES.map((state) => (
                    <SelectItem key={state} value={state}>
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pinCode">PIN Code</Label>
              <Input
                id="pinCode"
                placeholder="6-digit PIN code"
                maxLength={6}
                {...register('address.pinCode')}
                data-testid="input-pinCode"
              />
              {errors.address?.pinCode && (
                <p className="text-sm text-red-500">{errors.address.pinCode.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Emergency Contact */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Emergency Contact
            </CardTitle>
            <CardDescription>Person to contact in case of emergency</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label htmlFor="emergencyName">Name</Label>
              <Input
                id="emergencyName"
                placeholder="Contact name"
                {...register('emergencyContact.name')}
                data-testid="input-emergency-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="emergencyPhone">Phone</Label>
              <Input
                id="emergencyPhone"
                type="tel"
                placeholder="+91 98765 43210"
                {...register('emergencyContact.phone')}
                data-testid="input-emergency-phone"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="emergencyRelationship">Relationship</Label>
              <Select
                value={watch('emergencyContact.relationship')}
                onValueChange={(value) => setValue('emergencyContact.relationship', value)}
              >
                <SelectTrigger data-testid="select-relationship">
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Spouse">Spouse</SelectItem>
                  <SelectItem value="Father">Father</SelectItem>
                  <SelectItem value="Mother">Mother</SelectItem>
                  <SelectItem value="Son">Son</SelectItem>
                  <SelectItem value="Daughter">Daughter</SelectItem>
                  <SelectItem value="Brother">Brother</SelectItem>
                  <SelectItem value="Sister">Sister</SelectItem>
                  <SelectItem value="Friend">Friend</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Medical Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Heart className="h-5 w-5 text-red-500" />
              Medical Information
            </CardTitle>
            <CardDescription>Known allergies and chronic conditions</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="allergies">Allergies</Label>
              <Textarea
                id="allergies"
                placeholder="Enter allergies separated by commas (e.g., Penicillin, Sulfa drugs, Peanuts)"
                {...register('allergies')}
                data-testid="input-allergies"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="chronicConditions">Chronic Conditions</Label>
              <Textarea
                id="chronicConditions"
                placeholder="Enter conditions separated by commas (e.g., Diabetes, Hypertension, Asthma)"
                {...register('chronicConditions')}
                data-testid="input-conditions"
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(buildUrl('patients'))}
            data-testid="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!isFormValid || saving}
            data-testid="submit-btn"
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditMode ? 'Update Patient' : 'Create Patient'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PatientFormPage;
